/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Menu, { SubMenu } from 'rc-menu';
import { toast } from 'react-toastify';

import CodeIcon from '@material-ui/icons/Code';
import Divider from '@material-ui/core/Divider';
import FacebookIcon from '@material-ui/icons/Facebook';
import LinkIcon from '@material-ui/icons/Link';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MailIcon from '@material-ui/icons/MailOutline';
import PinterestIcon from '@material-ui/icons/Pinterest';

// app: components
import IconComponent from 'components/Icon';

// app modules: services
import { share, canShare } from 'services/socialShare';
import { openWindow } from 'services/inAppBrowser';
import { isApp, isAndroidApp } from 'services/environment';

// app: constants
import { COLOURS } from 'constants/index';

export function ShareMenuItems(props) {
  const { onDialogOpen, showEmbed, WrapperComponent, shareImageUrl } = props;

  const displayType = useSelector(state => state.dimensions.displayType);

  const handleShareLinkClick = async ({ url }) => {
    if (isAndroidApp() && window.cordova?.plugins?.clipboard) {
      await new Promise((resolve, reject) =>
        window.cordova.plugins.clipboard.copy(url, resolve, reject),
      );
    } else {
      await navigator.clipboard.writeText(url);
    }
    if (typeof props.onComplete === 'function') {
      props.onComplete();
    }
    toast.success(
      <p>
        <a href={url} target="_blank" className="link link--energized">
          Link
        </a>{' '}
        copied to clipboard
      </p>,
      {
        autoClose: 5000,
      },
    );
  };
  return [
    <WrapperComponent
      {...props}
      Icon={<FacebookIcon fontSize="large" htmlColor="#4867aa" />}
      onClick={({ url }) =>
        openWindow(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          '_blank',
        )
      }
      value="facebook"
      label="Facebook"
    />,
    <WrapperComponent
      {...props}
      Icon={<IconComponent name="x-big" width="27" className="dynamic-icon-colors x-icon" />}
      onClick={({ url, text }) =>
        openWindow(
          `http://twitter.com/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
            text,
          )}`,
          '_blank',
        )
      }
      value="x"
      label="X / Twitter"
      maxTextLength={280}
    />,
    shareImageUrl ? (
      <WrapperComponent
        {...props}
        Icon={<PinterestIcon fontSize="large" htmlColor="#cb1f27" />}
        onClick={({ url, text, image }) =>
          openWindow(
            `https://www.pinterest.co.uk/pin/create/button/?url=${encodeURIComponent(
              url,
            )}&description=${encodeURIComponent(text)}&media=${encodeURIComponent(image)}`,
            '_blank',
          )
        }
        value="pinterest"
        label="Pinterest"
        data-pin-custom
        data-pin-do="buttonPin"
        maxTextLength={160}
      />
    ) : null,
    <WrapperComponent
      {...props}
      Icon={<MailIcon fontSize="large" className="dynamic-icon-colors" />}
      onClick={({ url, text, title }) =>
        openWindow(
          `mailto:?subject=${title}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
          '_blank',
        )
      }
      value="email"
      label="Email"
    />,
    <Divider variant="fullWidth" />,
    showEmbed && !isApp() && displayType !== 'mobile' ? (
      <WrapperComponent
        {...props}
        Icon={CodeIcon}
        onClick={() =>
          import(/* webpackChunkName: 'embed-dialog' */ 'components/EmbedDialog').then(module =>
            onDialogOpen(module.default),
          )
        }
        value="embed"
        label="Embed"
      />
    ) : null,
    <WrapperComponent
      {...props}
      Icon={LinkIcon}
      onClick={handleShareLinkClick}
      value="shareLink"
      label="Get shareable link"
    />,
    canShare() ? (
      <WrapperComponent
        {...props}
        Icon={<IconComponent fill={COLOURS.ENERGIZED} name="native-share" height="30" width="30" />}
        onClick={share}
        value="other"
        label="More..."
      />
    ) : null,
  ];
}

export function ShareMenu(props) {
  return <Menu selectable={false}>{ShareMenuItems(props)}</Menu>;
}

function ShareItem(props) {
  const {
    ItemComponent,
    className,
    iconClassName,
    text,
    heading,
    popup,
    onComplete,
    onClick,
    showEmbed,
    ...others
  } = props;
  const content = (
    <React.Fragment>
      <ListItemIcon className={iconClassName}>
        <IconComponent fill={COLOURS.ENERGIZED} name="native-share" height="30" width="30" />
      </ListItemIcon>
      <ListItemText primary="Share" />
    </React.Fragment>
  );

  if (popup) {
    return (
      <SubMenu
        selectable={false}
        className={className}
        popupOffset={[-5, 0]}
        title={content}
        {...others}
      >
        {ShareMenuItems(props)}
      </SubMenu>
    );
  }

  return (
    <ItemComponent className={className} button onClick={onClick} {...others}>
      {content}
    </ItemComponent>
  );
}

ShareItem.propTypes = {
  heading: PropTypes.string,
  ItemComponent: PropTypes.elementType.isRequired,
  WrapperComponent: PropTypes.elementType.isRequired,
  onClick: PropTypes.func,
  onComplete: PropTypes.func,
  popup: PropTypes.bool,
  text: PropTypes.string,
};

ShareItem.defaultProps = {
  heading: '',
  onClick: null,
  onComplete: () => {},
  popup: false,
  text: 'Share...',
};

ShareMenuItems.propTypes = {
  ...ShareItem.propTypes,
  onDialogOpen: PropTypes.func,
  showEmbed: PropTypes.bool,
};

ShareMenuItems.defaultProps = {
  ...ShareItem.defaultProps,
  onDialogOpen: () => {},
  showEmbed: true,
};

ShareMenu.propTypes = {
  ...ShareItem.propTypes,
  onDialogOpen: PropTypes.func,
  showEmbed: PropTypes.bool,
};

ShareMenu.defaultProps = {
  ...ShareItem.defaultProps,
  onDialogOpen: () => {},
  showEmbed: true,
};

export default ShareItem;
